import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { PlacementService } from '../../core/services/placement-service/placement-service.service';
import { TestConversationItem, TestQuestion, QuestionOption } from '../../core/models/placement/placement.model';
import { ConfirmationDialogComponent } from '../../shared/confirmation-dialog/confirmation-dialog.component';
import { ToastrService } from 'ngx-toastr';
import { SharedModule } from '../../shared/shared.module';
import { LoaderStep } from '../../shared/test-generation-loader/test-generation-loader.component';

export type QuestionStatus = 'current' | 'answered' | 'review' | 'not-answered' | 'visited';



@Component({
  selector: 'app-test-screen',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatDialogModule, SharedModule],
  templateUrl: './test-screen.component.html',
  styleUrls: ['./test-screen.component.scss']
})
export class TestScreenComponent implements OnInit, OnDestroy {
  @Input() questions: TestQuestion[] = [];
  @Input() testTitle: string = 'Resume Test';
  @Input() durationMinutes: number = 30;
  @Input() testId: string = '';
  @Input() attemptId: string = '';
  @Input() resumeData: any = null;
  currentQuestionIndex: number = 0;
  answers: Map<string, string> = new Map();
  reviewList: Set<string> = new Set();
  visitedSet: Set<string> = new Set();
  skippedSet: Set<string> = new Set();

  timerDisplay: string = '00:00';
  private timerInterval: ReturnType<typeof setInterval> | undefined;
  private secondsElapsed: number = 0;

  isSubmitting: boolean = false;
  isQuitting: boolean = false;

  readonly evaluationSteps: LoaderStep[] = [
    { text: 'Reading your responses...', icon: 'description' },
    { text: 'Comparing with expected answers...', icon: 'compare_arrows' },
    { text: 'Calculating topic-wise accuracy...', icon: 'bar_chart' },
    { text: 'Identifying your strong areas...', icon: 'star' },
    { text: 'Spotting areas for improvement...', icon: 'trending_up' },
    { text: 'Preparing your detailed report...', icon: 'summarize' },
  ];

  constructor(
    private placementService: PlacementService,
    private router: Router,
    private dialog: MatDialog,
    private _toastr: ToastrService
  ) { }

  ngOnInit(): void {
    const state = window.history.state;

    if (!this.testId && state && state.testId) {
      this.testId = state.testId;
    }
    if (this.questions.length === 0 && state && state.questions) {
      this.questions = state.questions || [];
    }
    if (!this.durationMinutes && state && state.durationMinutes) {
      this.durationMinutes = state.durationMinutes || 30;
    }
    if (!this.testTitle || this.testTitle === 'Resume Test') {
      this.testTitle = state?.testTitle || 'Placement Test';
    }

    const resumeDataSource = this.resumeData || state?.resumeData;
    if (resumeDataSource) {
      const data = resumeDataSource;

      if (!this.attemptId) {
        this.attemptId = data.attempt_id;
      }

      this.testId = data.test_id || this.testId;
      if (!this.testTitle || this.testTitle === 'Resume Test') {
        this.testTitle = state?.testTitle || 'Placement Test';
      }

      if (data.test_conversation) {
        this.questions = data.test_conversation.map((item: TestConversationItem) => item.question as TestQuestion);

        data.test_conversation.forEach((item: TestConversationItem) => {
          if (item.user_answer) {
            this.answers.set(item.question.question_id, item.user_answer);
          }
          if (item.is_skipped) {
            this.skippedSet.add(item.question.question_id);
          }
          if (item.is_marked_for_review) {
            this.reviewList.add(item.question.question_id);
}
        });
      }

      if (data.time_taken) {
        this.secondsElapsed = Number(data.time_taken);
      }
    }

    this.loadStateFromStorage();


    if (this.questions.length > 0) {
      this.saveStateToStorage();
    }

    if (this.questions.length === 0) {
      console.warn('No questions found in state. If you refreshed, ensure your logic handles reloading questions.');

    }


    if (state && state.attemptId && state.attemptId !== this.attemptId) {
      this.clearStateFromStorage(); // Clear old data

      this.attemptId = state.attemptId;
      this.answers.clear();
      this.reviewList.clear();
      this.skippedSet.clear();
      this.visitedSet.clear();
      this.secondsElapsed = 0;



      this.saveStateToStorage(); // Save new fresh state
    }

    if (!this.attemptId) {
      const stateAttemptId = state?.attemptId;
      if (stateAttemptId) {
        this.attemptId = stateAttemptId;
        this.saveStateToStorage(); // Save immediately
      } else if (this.testId) {

        this.placementService.startAttempt(this.testId).subscribe({
          next: (res) => {
            if (res.Success && res.Data) {
              this.attemptId = res.Data.attempt_id;
              this.saveStateToStorage(); // Save new attempt ID immediately
            }
          },
          error: (err) => {
            console.error('Failed to start attempt:', err);
          }
        });
      }
    }












    this.startTimer();
    if (this.questions.length > 0) {

      const firstUnanswered = this.questions.findIndex(q => !this.answers.has(q.question_id));
      if (firstUnanswered !== -1) {
        this.visitQuestion(firstUnanswered);
      } else {
        this.visitQuestion(0);
      }
    }
  }

  ngOnDestroy(): void {
    this.stopTimer();


    if (!this.isSubmitting && !this.isQuitting && this.attemptId) {
      this.saveOnLeave();
    }
  }

  private saveOnLeave() {




    let targetQuestionToAdd: TestQuestion | undefined = this.currentQuestion;

    if (!targetQuestionToAdd && this.questions.length > 0) {
      targetQuestionToAdd = this.questions[0];
    }

    if (targetQuestionToAdd) {
      const qId = targetQuestionToAdd.question_id;
      const userAnswer = this.answers.get(qId) || '';
      const isSkipped = this.skippedSet.has(qId);

      const conversation: TestConversationItem[] = [{
        question: { question_id: targetQuestionToAdd.question_id },
        user_answer: userAnswer,
        is_skipped: isSkipped,
        is_marked_for_review: this.reviewList.has(qId),
        evaluation: null
      }];



      this.placementService.saveProgress(this.attemptId, conversation, this.secondsElapsed).subscribe({
        next: () => console.log('Progress saved on leave'),
        error: (err) => console.error('Error saving progress on leave', err)
      });
    }
  }

  get currentQuestion(): TestQuestion | undefined {
    return this.questions[this.currentQuestionIndex];
  }

  startTimer() {
    this.stopTimer(); // Clear any existing timer first
    this.updateTimerDisplay();
    this.timerInterval = setInterval(() => {
      this.secondsElapsed++;
      this.updateTimerDisplay();

    }, 1000);
  }


 private loadStateFromStorage() {

  if (!this.testId) {
    const lastId = localStorage.getItem('last_active_test_id');

    if (lastId) {
      this.testId = lastId;
    }
  }

  if (!this.attemptId) return;

  const savedState = localStorage.getItem(
    `placement_test_state_${this.attemptId}`
  );

  if (savedState) {

    const parsed = JSON.parse(savedState);

    this.answers = new Map(parsed.answers || []);

    this.reviewList = new Set(parsed.reviewList || []);

    this.skippedSet = new Set(parsed.skippedList || []);

    this.visitedSet = new Set(parsed.visitedList || []);

    this.currentQuestionIndex =
      parsed.currentQuestionIndex || 0;

    this.secondsElapsed =
      parsed.secondsElapsed || 0;
  }
}



 private saveStateToStorage() {

  if (this.testId) {
    localStorage.setItem('last_active_test_id', this.testId);
  }

  const stateData = {
    answers: Array.from(this.answers.entries()),
    reviewList: Array.from(this.reviewList),
    skippedList: Array.from(this.skippedSet),
    visitedList: Array.from(this.visitedSet),
    currentQuestionIndex: this.currentQuestionIndex,
    secondsElapsed: this.secondsElapsed
  };

  localStorage.setItem(
    `placement_test_state_${this.attemptId}`,
    JSON.stringify(stateData)
  );
}

 private clearStateFromStorage() {

  localStorage.removeItem('last_active_test_id');

  if (this.attemptId) {
    localStorage.removeItem(
      `placement_test_state_${this.attemptId}`
    );
  }
}

  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  updateTimerDisplay() {
    const minutes = Math.floor(this.secondsElapsed / 60);
    const seconds = this.secondsElapsed % 60;
    this.timerDisplay = `${minutes.toString().padStart(2, '0')} : ${seconds.toString().padStart(2, '0')}`;
  }

  visitQuestion(index: number) {
    if (index >= 0 && index < this.questions.length) {
      this.currentQuestionIndex = index;
      const qId = this.questions[index].question_id;
      this.visitedSet.add(qId);
      this.saveStateToStorage();
    }
  }

  nextQuestion() {
    this.saveCurrentProgress(false);

    let foundNext = false;
    for (let i = this.currentQuestionIndex + 1; i < this.questions.length; i++) {
      if (!this.answers.has(this.questions[i].question_id)) {
        this.visitQuestion(i);
        foundNext = true;
        break;
      }
    }

    if (!foundNext) {

      for (let i = 0; i < this.currentQuestionIndex; i++) {
        if (!this.answers.has(this.questions[i].question_id)) {
          this._toastr.info('Cycling back to unanswered questions.');
          this.visitQuestion(i);
          foundNext = true;
          break;
        }
      }
    }

    if (!foundNext) {
      if (this.isAllAnswered) {
        this._toastr.success('You have answered all questions. Ready to submit?');
      } else {
        this._toastr.info('This is the last unanswered question.');
      }
    }
  }

  private handleEndOfListNavigation() {


    const nextTargetIndex = this.questions.findIndex(q => {
      const isReview = this.reviewList.has(q.question_id);
      const isAnswered = this.answers.has(q.question_id);

      return isReview || !isAnswered;
    });

    if (nextTargetIndex !== -1) {
      if (nextTargetIndex === this.currentQuestionIndex) {

        this._toastr.info('This is the last question needing attention.');
      } else {
        this._toastr.info('Cycling back to reviewable or skipped questions.');
        this.visitQuestion(nextTargetIndex);
      }
    } else {

      this._toastr.success('You have completed all questions. Ready to submit?');
    }
  }

  prevQuestion() {
    if (this.currentQuestionIndex > 0) {
      this.visitQuestion(this.currentQuestionIndex - 1);
    }
  }

  skipQuestion() {

    if (this.currentQuestion) {
      this.skippedSet.add(this.currentQuestion.question_id);
      this.saveCurrentProgress(true);
    }
    if (this.currentQuestionIndex < this.questions.length - 1) {
      this.visitQuestion(this.currentQuestionIndex + 1);
    }
  }

  saveCurrentProgress(isSkipped: boolean) {
    if (!this.attemptId || !this.currentQuestion || this.isSubmitting) return;

    const questionId = this.currentQuestion.question_id;
    const userAnswer = this.answers.get(questionId) || '';

    const conversation: TestConversationItem[] = [{
      question: { question_id: questionId },
      user_answer: userAnswer,
      is_skipped: isSkipped,
      is_marked_for_review: this.reviewList.has(questionId),
      evaluation: null
    }];

    this.placementService.saveProgress(this.attemptId, conversation, this.secondsElapsed).subscribe({
      next: (res) => {
      },
      error: (err) => {
        console.error('Failed to save progress:', err);
      }
    });
  }

  goToQuestion(index: number) {
    this.visitQuestion(index);
  }



  toggleReview() {

  if (!this.currentQuestion) return;

  const qId = this.currentQuestion.question_id;

  if (this.reviewList.has(qId)) {
    this.reviewList.delete(qId);
  } else {
    this.reviewList.add(qId);
  }

  this.saveStateToStorage();

  if (this.currentQuestionIndex < this.questions.length - 1) {
    this.visitQuestion(this.currentQuestionIndex + 1);
  }
}

  clearResponse() {
    if (!this.currentQuestion) return;
    this.answers.delete(this.currentQuestion.question_id);
    this.reviewList.delete(this.currentQuestion.question_id);
    this.saveStateToStorage();
  }

  submitTest() {
    if (!this.attemptId) return;

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Submit Test',
        message: 'Are you sure you want to submit? You cannot change your answers after submission.',
        confirmText: 'Submit Now',
        cancelText: 'Continue Test',
        type: 'submit'
      },
      width: '400px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.executeSubmit();
      }
    });
  }

  private executeSubmit() {
    this.isSubmitting = true;

    this.stopTimer();

    const testConversation: TestConversationItem[] = this.questions.map(q => {
      const qId = q.question_id;
      return {
        question: { question_id: q.question_id },
        user_answer: this.answers.get(q.question_id) || '',
        is_skipped: !this.answers.has(q.question_id), // Logic: skipped if not answerMap
        is_marked_for_review: this.reviewList.has(q.question_id),
        evaluation: null
      };
    });

    this.placementService.submitTest(this.attemptId, testConversation, this.secondsElapsed).subscribe({
      next: (res) => {
        this.isSubmitting = false;
        if (res.Success && res.Data) {

          this.clearStateFromStorage();

          this.router.navigate(['/student-dashboard/placement-test-result'], {
            state: { resultData: res.Data },
            replaceUrl: true
          });
        }
      },
      error: (err) => {
        this.isSubmitting = false;
        console.error('Failed to submit test:', err);
        this._toastr.error('Failed to submit test. Please try again.');
      }
    });
  }

  quitTest() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Quit Test',
        message: 'Are you sure you want to quit? Your test attempt will be deleted and you will need to start over.',
        confirmText: 'Quit Test',
        cancelText: 'Stay Here',
        type: 'quit'
      },
      width: '400px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isQuitting = true;

        if (this.attemptId) {
          this.placementService.deleteAttempt(this.attemptId).subscribe({
            next: (res) => {
              this.clearStateFromStorage();
              this.navigateToHome();
            },
            error: (err) => {
              console.error('Failed to delete attempt:', err);

              this.clearStateFromStorage();
              this.navigateToHome();
            }
          });
        } else {

          this.clearStateFromStorage();
          this.navigateToHome();
        }
      }
    });
  }

  private navigateToHome() {

    setTimeout(() => {
      this.router.navigate(['/student-dashboard/placement-prep']);
    }, 1500);
  }

  getQuestionStatus(question: TestQuestion, index: number): string {
    const qId = question.question_id;
    const isCurrent = index === this.currentQuestionIndex;

    if (isCurrent) return 'current';
    if (this.reviewList.has(qId)) return 'review';
    if (this.answers.has(qId)) return 'answered';
    if (this.visitedSet.has(qId)) return 'not-answered';

    return 'not-visited';
  }

  getOptionLabel(index: number): string {
    return String.fromCharCode(65 + index); // A, B, C, D...
  }

  selectOption(option: QuestionOption) {
    if (!this.currentQuestion) return;

    this.answers.set(this.currentQuestion.question_id, option.text);

    this.skippedSet.delete(this.currentQuestion.question_id);

    this.saveStateToStorage();
  }

  isOptionSelected(optionText: string): boolean {
    if (!this.currentQuestion) return false;
    return this.answers.get(this.currentQuestion.question_id) === optionText;
  }

  get isAllAnswered(): boolean {
    return this.questions.length > 0 && this.answers.size === this.questions.length;
  }
}
